import { useMemo } from "react";
import {
  useV2ListServiceCategorysForAccountQuery,
  useV2CreateServiceCategoryMutation,
  useV2UpdateServiceCategoryMutation,
  useV2DeleteServiceCategoryMutation,
  useV2ChangeServiceCategoryPositionByIdMutation,
} from "@generated";
import type { V2ServiceCategoryResource } from "@generated";
import { serverCategoryRow } from "../types";
import type { ServiceCategoryRow } from "../types";
import { planServiceCategorySync } from "../LineOfBusinessForm/ServiceCategoriesEditor/diff";

interface CategoryValues {
  name: string;
  code: string;
  position?: number;
}

// The parent LOB is set via the writable `lob-id` attribute (the resource no
// longer exposes a line-of-business relationship, only a `lob-id` attr + a
// top-level `filter[lob]`). Cast because the generated type omits `lob-id` from
// the create surface in some codegen passes.
export const buildServiceCategoryData = (
  values: CategoryValues,
  lobId: string,
  id?: string
): V2ServiceCategoryResource =>
  ({
    type: "service-categories",
    ...(id ? { id } : {}),
    attributes: {
      name: values.name,
      code: values.code,
      "lob-id": Number(lobId),
      ...(values.position != null ? { position: values.position } : {}),
    },
  } as unknown as V2ServiceCategoryResource);

const movePositionData = (
  id: string,
  position: number
): V2ServiceCategoryResource =>
  ({
    type: "service-categories",
    id: id,
    attributes: { position },
  } as unknown as V2ServiceCategoryResource);

// Service categories are fetched via their own list query filtered to the LOB
// and sorted by position (the LOB resource no longer exposes them as an
// includable relationship — only a `filter[lob]`).
export const useServiceCategoriesList = (lobId: string) => {
  const { data, isFetching, refetch } =
    useV2ListServiceCategorysForAccountQuery(
      { filter: { lob: lobId }, sort: ["position"], page: { size: 500 } },
      { refetchOnMountOrArgChange: true }
    );

  const rows = useMemo<ServiceCategoryRow[]>(
    () =>
      (data?.data ?? []).map((c: any) =>
        serverCategoryRow(
          String(c.id),
          c.attributes?.name ?? "",
          c.attributes?.code ?? ""
        )
      ),
    [data]
  );

  return { rows, isFetching, refetch };
};

export const useServiceCategorySync = () => {
  const [create] = useV2CreateServiceCategoryMutation();
  const [update] = useV2UpdateServiceCategoryMutation();
  const [destroy] = useV2DeleteServiceCategoryMutation();
  const [move] = useV2ChangeServiceCategoryPositionByIdMutation();

  const syncServiceCategories = async (
    lobId: string,
    initial: ServiceCategoryRow[],
    current: ServiceCategoryRow[]
  ) => {
    const plan = planServiceCategorySync(initial, current);

    for (const id of plan.deletes) {
      await destroy({ id: id }).unwrap();
    }

    const newIdByKey: Record<string, string> = {};
    for (const c of plan.creates) {
      const res: any = await create({
        body: { data: buildServiceCategoryData(c, lobId) },
      }).unwrap();
      if (res?.data?.id != null) newIdByKey[c.clientKey] = String(res.data.id);
    }

    for (const u of plan.updates) {
      await update({
        id: u.id,
        body: { data: buildServiceCategoryData(u, lobId, u.id) },
      }).unwrap();
    }

    // Normalize positions when anything was added or moved. Applying absolute
    // positions over the live rows in display order converges regardless of how
    // acts_as_list shifts siblings on each move.
    if (plan.creates.length > 0 || plan.reorders.length > 0) {
      const live = current.filter((r) => !r._deleted);
      for (let i = 0; i < live.length; i++) {
        const serverId = live[i].id ?? newIdByKey[live[i].clientKey];
        if (!serverId) continue;
        await move({
          id: serverId,
          body: { data: movePositionData(serverId, i + 1) },
        }).unwrap();
      }
    }
  };

  return { syncServiceCategories };
};
